import { useContext } from 'react';

import { AppContext } from '../../AppContext';
import { Config } from '../../config';
import { mergeUrlParameters } from '../../helpers/uri';
import { ShopifyContext } from '../ShopifyProvider';
import { CartContext } from './CartProvider';

/**
 * Builds a Shopify checkout URL using the /cart/add?items[] format. This format supports selling
 * plans via items[N][selling_plan]=ID.
 *
 * Phase 2: selling plan IDs now come from cart lines, not the product catalog.
 *
 * @see https://community.shopify.com/t/pre-load-cart-with-subscription-plan-products-using-permalinks/35303/4
 * Note: URL length may become a concern if carts grow large. For now, customers
 * typically purchase 1-2 items, so this is acceptable.
 */
const useCheckoutUrl = (): URL => {
  const { userLocation, getCurrentUrl, currentLocale, cookieManager } = useContext(AppContext);
  const cart = useContext(CartContext);
  const { store } = useContext(ShopifyContext);
  const { options } = Config.shopify ?? {};
  const gaCookie = cookieManager.getCookie('_ga');

  // Build items[] params from cart lines.
  const itemParams: string[] = [];

  cart.lines.forEach((line, index) => {
    const numericId = line.optionId.match(/\/(\d+)/)?.[1];

    if (!numericId) return;

    itemParams.push(`items[${index}][id]=${numericId}`);
    itemParams.push(`items[${index}][quantity]=${line.quantity}`);

    // Selling plan is stored per-line — no catalog lookup needed.
    if (line.sellingPlanId) {
      const sellingPlanNumericId = line.sellingPlanId.match(/\/(\d+)/)?.[1];

      if (sellingPlanNumericId) {
        itemParams.push(`items[${index}][selling_plan]=${sellingPlanNumericId}`);
      }
    }
  });

  let queryParams: Record<string, string | boolean> = {
    'attributes[locale]': currentLocale,
    locale: currentLocale,
    'checkout[shipping_address][country]': userLocation.country,
    access_token: store?.storefrontAccessToken ?? '',
  };

  if (options?.checkoutQueryParams) {
    queryParams = {
      ...queryParams,
      ...options.checkoutQueryParams,
    };
  }

  if (gaCookie) {
    queryParams._ga = gaCookie;
  }

  // Add discount code if set. This supports the waitlist flow: a user receives a link
  // with a discount code (e.g. ?discount=WAITLIST123) that gets applied at Shopify checkout.
  // TODO: Needs client-side validation logic later. Currently codes are only validated by
  // Shopify at checkout — invalid codes show an error on Shopify's page.
  if (cart.discountCode) {
    queryParams.discount = cart.discountCode;
  }

  const queryString = Object.entries(queryParams)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

  // Build: /cart/add?items[0][id]=...&items[0][quantity]=...&...&return_to=/checkout?note=...
  const itemsQuery = itemParams.join('&');
  const returnTo = encodeURIComponent(`/checkout?${queryString}`);
  const shopifyUrl = new URL(
    `https://${store?.domain}/cart/add?${itemsQuery}&return_to=${returnTo}`
  );
  mergeUrlParameters(shopifyUrl, new URL(getCurrentUrl()), Config.persistentQueryParams);
  return shopifyUrl;
};

export { useCheckoutUrl };
